import { useCallback, useState } from "react";

import {
  createAuthController,
  createAxiosHttpClient,
  createHttpAuthRepository,
  useAuthStore,
  useLanguageStore,
  useSessionTimeout,
  type AuthController,
  type AuthRepository,
  type HttpClient,
} from "@nasam/core";

const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;
const MAX_IDLE_PROMPT_MS = 60_000;

// Set once the router exists (see router.tsx's createAppRouter) -- this
// module is imported by router.tsx itself, so the router can't be a
// constructor argument here without a cycle.
let forbiddenHandler: (() => void) | undefined;

export function setForbiddenHandler(handler: () => void): void {
  forbiddenHandler = handler;
}

export const authHttpClient: HttpClient = createAxiosHttpClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL || "",
  getAccessToken: () => useAuthStore.getState().accessToken,
  getLanguage: () => useLanguageStore.getState().language,
  refreshAccessToken: async () => {
    const session = await authRepository.refreshAccessToken();
    useAuthStore.getState().setSession(session.accessToken, session.user);
    return session.accessToken;
  },
  onRefreshFailure: () => {
    useAuthStore.getState().clearSession("expired");
  },
  onForbidden: () => forbiddenHandler?.(),
});

export const authRepository: AuthRepository =
  createHttpAuthRepository(authHttpClient);

export const authController: AuthController = createAuthController(
  authRepository,
  useAuthStore,
);

export interface AppSessionTimeout {
  /** The pre-logout warning is up (only while still authenticated). */
  readonly promptOpen: boolean;
  /** Milliseconds left before the idle logout fires. */
  readonly getRemainingTime: () => number;
  readonly isPrompted: () => boolean;
  /** Dismisses the warning and restarts the idle timer, across every tab. */
  readonly staySignedIn: () => void;
  readonly closePrompt: () => void;
}

export function useAppSessionTimeout(): AppSessionTimeout {
  const authenticated = useAuthStore(
    (state) => state.accessToken !== null && state.user !== null,
  );
  const configuredMinutes = Number(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES);
  const timeoutMinutes =
    Number.isFinite(configuredMinutes) && configuredMinutes > 0
      ? configuredMinutes
      : DEFAULT_IDLE_TIMEOUT_MINUTES;
  // Capped at half the timeout so a short (testing) timeout still leaves the
  // user some active time before the warning shows.
  const promptBeforeIdleMs = Math.min(
    MAX_IDLE_PROMPT_MS,
    (timeoutMinutes * 60_000) / 2,
  );
  const [prompted, setPrompted] = useState(false);

  const { activate, getRemainingTime, isPrompted } = useSessionTimeout({
    authenticated,
    timeoutMinutes,
    promptBeforeIdleMs,
    onPrompt: () => setPrompted(true),
    onTimeout: () => {
      setPrompted(false);
      void authController.logout("timeout");
    },
  });

  const staySignedIn = useCallback(() => {
    activate();
    setPrompted(false);
  }, [activate]);
  const closePrompt = useCallback(() => setPrompted(false), []);

  return {
    promptOpen: prompted && authenticated,
    getRemainingTime,
    isPrompted,
    staySignedIn,
    closePrompt,
  };
}
